import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Iter-78 pt 9 — canonical color palette per CEO directive:
//   • Green  = trash pickup completed
//   • Blue   = recycle pickup
//   • Yellow = trash placed by resident · ready to be collected
//   • Orange = no trash placement (crew visited, nothing to pick up)
//   • Red    = active violation
const SIGNALS = [
  {
    key: 'green',
    label: 'Trash pickup completed',
    swatch: 'bg-emerald-500/25 border-emerald-400',
    text:   'text-emerald-100',
    hint:   'Trash bag collected by the crew (or manually confirmed with a photo). Unit is done for the night.',
  },
  {
    key: 'blue',
    label: 'Recycle pickup',
    swatch: 'bg-blue-500/25 border-blue-500',
    text:   'text-blue-100',
    hint:   'Recycling bin collected on the recycling-service night. Separate cadence from trash pickup.',
  },
  {
    key: 'yellow',
    label: 'Trash ready to be collected',
    swatch: 'bg-amber-400/25 border-amber-400',
    text:   'text-amber-100',
    hint:   'Trash is ready for collection. Crew must service every yellow unit — buildings with yellow units are locked until confirmed.',
  },
  {
    key: 'orange',
    label: 'No trash placement',
    swatch: 'bg-orange-500/25 border-orange-500',
    text:   'text-orange-100',
    hint:   'Crew visited the unit and there was nothing outside to pick up. Nothing more to do tonight for this unit.',
  },
  {
    key: 'red',
    label: 'Active violation',
    swatch: 'bg-red-500/25 border-red-500',
    text:   'text-red-100',
    hint:   'A violation was reported on this unit. Property Management and the account holder are notified.',
  },
  { key: 'gray', label: 'Bulk Item Removal', swatch: 'bg-gray-400/25 border-gray-400', text: 'text-gray-100', hint: 'Bulk item removal requested. Items must be staged beside the trash room or dumpster.' },
  { key: 'dark_gray', label: 'Request Trashcan', swatch: 'bg-zinc-700 border-zinc-500', text: 'text-zinc-100', hint: 'Trashcan requested. A $50 replacement fee is due only after delivery and is added manually to the next monthly rent bill.' },
];

export const ServiceSignalsLegend = ({ propertyId, propertyIds, showCounts = true, className = '' }) => {
  const [counts, setCounts] = useState(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const update = () => setRefresh((n) => n + 1);
    window.addEventListener('necs:service-signals-changed', update);
    return () => window.removeEventListener('necs:service-signals-changed', update);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!showCounts) return;
      const ids = propertyId
        ? [propertyId]
        : (Array.isArray(propertyIds) ? propertyIds.filter(Boolean) : []);
      if (ids.length === 0) return;
      try {
        const results = await Promise.all(
          ids.slice(0, 25).map((pid) =>
            fetch(`${BACKEND_URL}/api/ops/properties/${pid}/unit-statuses`, { credentials: 'include' })
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null),
          ),
        );
        if (cancelled) return;
        const merged = { green: 0, orange: 0, red: 0, blue: 0, yellow: 0, gray: 0, dark_gray: 0 };
        results.forEach((r) => {
          if (r?.counts) {
            merged.green  += r.counts.green  || 0;
            merged.orange += r.counts.orange || 0;
            merged.red    += r.counts.red    || 0;
            merged.blue   += r.counts.blue   || 0;
            merged.yellow += r.counts.yellow || 0;
            merged.gray += r.request_signal_counts?.gray || 0;
            merged.dark_gray += r.request_signal_counts?.dark_gray || 0;
          }
        });
        setCounts(merged);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [propertyId, propertyIds, showCounts, refresh]);

  return (
    <Card
      className={`bg-zinc-900/70 border border-zinc-800 p-3 ${className}`}
      data-testid="service-signals-legend"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Info className="h-3.5 w-3.5 text-zinc-400" />
        <p className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">
          Service signals · what the colors mean
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {SIGNALS.map((s) => {
          const count = counts?.[s.key];
          return (
            <div
              key={s.key}
              className={`rounded-md border p-2 ${s.swatch}`}
              data-testid={`service-signal-${s.key}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className={`text-[11px] font-bold ${s.text}`}>{s.label}</span>
                {counts && (
                  <Badge className="bg-black/40 border border-white/10 text-white text-[10px]" data-testid={`service-signal-count-${s.key}`}>
                    {count ?? 0}
                  </Badge>
                )}
              </div>
              <p className={`text-[10px] leading-snug ${s.text} opacity-80`}>{s.hint}</p>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
